/**
 * Frozen API contract types for the Green Line Hub client API (v1).
 * These mirror the middleware contract exactly — do not extend or reshape
 * them client-side. If the server contract changes, it changes here first.
 */

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface Contact {
  firstName: string
  lastName: string
  email: string
  accountId: string
  accountName: string
  /** Present (true) on staff impersonation sessions — writes are attributed to the actor. */
  impersonated?: boolean
  /** Impersonation actor display name (the staff member acting). */
  actorName?: string
}

/** POST /v1/client/auth/start — always 200 (no account enumeration). */
export interface AuthStartResponse {
  ok: true
}

/** POST /v1/client/auth/complete — 200. */
export interface AuthCompleteResponse {
  sessionJwt: string
  contact: Contact
  /** Present for email deep-links: the in-app path to land on (e.g. "/orders/…"). */
  returnTo?: string
}

export type AuthCompleteErrorCode = 'expired_link' | 'invalid_link' | 'link_already_used'

/** POST /v1/client/auth/complete — 401 body. */
export interface AuthCompleteError {
  error: AuthCompleteErrorCode
  message: string
}

// ---------------------------------------------------------------------------
// Orders (GLI sales orders — SAP-synced Opportunities)
// ---------------------------------------------------------------------------

export type StageGroup = 'request' | 'open' | 'closed'
export type OrderListFilter = 'open' | 'closed' | 'all'

export interface OrderHeader {
  id: string
  name: string
  /** Raw Opportunity StageName — labels/groups come from src/lib/status.ts. */
  stage: string
  docEntry: string | null
  docNum: string | null
  poNumber: string | null
  amount: number | null
  currency: string
  requiredDate: string | null
  dueDate: string | null
  createdAt: string
  closedAt: string | null
  lineCount: number
  stageLabel: string
  statusGroup: StageGroup
  /** Accepted but the SAP release hasn't written the DocNum back yet. */
  syncPending: boolean
}

/** GET /v1/client/orders?status=open|closed|all — 200. */
export interface OrdersListResponse {
  orders: OrderHeader[]
  counts: { open: number; closed: number; all: number }
}

export interface OrderLine {
  id: string
  productName: string
  itemCode: string | null
  quantity: number
  uom: string | null
  packSizeKg: number | null
  pricePerKg: number | null
  unitPrice: number
  lineTotal: number
  quantityShipped: number | null
  quantityOutstanding: number | null
  sapLineStatus: string | null
  shipDate: string | null
}

export interface Shipment {
  id: string
  docNum: string | null
  status: string | null
  carrier: string | null
  trackingNumber: string | null
  shippedAt: string | null
  docEntry: number | null
}

/** GET /v1/client/orders/:id — 200. */
export interface OrderDetail extends OrderHeader {
  lines: OrderLine[]
  shipments: Shipment[]
  invoices: InvoiceHeader[]
}

/**
 * GET /v1/client/orders/:id/fulfillment — 200. Apex DTO passthrough keyed by
 * line id. Loosely typed on purpose: the org owns the shape, the Hub reads
 * `status`/`statusDetail` defensively and must tolerate absent line ids.
 */
export type FulfillmentEntry = Record<string, unknown>
export type OrderFulfillmentResponse = Record<string, FulfillmentEntry>

// ---------------------------------------------------------------------------
// Purchase requests (Hub-submitted drafts)
// ---------------------------------------------------------------------------

export type PurchaseRequestStatus = 'submitted' | 'accepted' | 'declined'

export interface PurchaseRequestSummary {
  id: string
  name: string
  status: PurchaseRequestStatus
  /** Present on accepted requests — the live order's stage label. */
  stageLabel?: string
  submittedAt: string | null
  submittedByName: string | null
  poNumber: string | null
  amount: number | null
  currency: string
  lineCount: number
  reviewNote: string | null
  /** Present on accepted requests: the request lives on as this order. */
  orderId?: string
}

/** GET /v1/client/purchasing — 200. */
export interface PurchasingListResponse {
  requests: PurchaseRequestSummary[]
}

/** GET /v1/client/purchasing/:id — 200. */
export interface PurchaseRequestDetail extends PurchaseRequestSummary {
  lines: OrderLine[]
}

/**
 * One requested line: the chosen pack option (priceBreakId must be one of the
 * product's active breaks in the account pricebook) and a whole number of
 * packs (>= 1). The server derives pack size, per-kg price, and pack label
 * from the validated break — the client sends no pricing.
 */
export interface PurchaseRequestLineInput {
  productId: string
  priceBreakId: string
  quantity: number
  dueDate?: string
  description?: string
}

/** The `payload` JSON field of the multipart POST /v1/client/purchasing. */
export interface SubmitPurchaseRequestPayload {
  poNumber: string
  requestedDate?: string
  shipToCode?: string
  payToCode?: string
  lines: PurchaseRequestLineInput[]
}

// Server-enforced caps on POST /v1/client/purchasing — the wizard mirrors them
// client-side and the MSW handler enforces them so tests catch drift.
export const MAX_PURCHASE_REQUEST_LINES = 50
export const MAX_PACKS_PER_LINE = 10_000
export const MAX_PO_NUMBER_LENGTH = 100

/** POST /v1/client/purchasing (multipart: payload + one PO file) — 201. */
export interface SubmitPurchaseRequestResponse {
  id: string
  name: string
}

// ---------------------------------------------------------------------------
// Quotes (view-only in v1)
// ---------------------------------------------------------------------------

export interface QuoteHeader {
  id: string
  docNum: string | null
  status: string | null
  issueDate: string | null
  expirationDate: string | null
  paymentTerms: string | null
  shippingTerms: string | null
  notes: string | null
  total: number
}

/** GET /v1/client/quotes — 200. */
export interface QuotesListResponse {
  quotes: QuoteHeader[]
}

/**
 * Quote lines display quantity + uom + lineTotal VERBATIM — synced rows carry
 * raw SAP UoM values, so the Hub never recomputes per-kg math.
 */
export interface QuoteLine {
  id: string
  productName: string | null
  quantity: number | null
  pricePerUnit: number | null
  uom: string | null
  packSize: string | null
  leadTime: string | null
  supplyModel: string | null
  lineTotal: number | null
}

/** GET /v1/client/quotes/:id — 200 (header nested, unlike orders). */
export interface QuoteDetailResponse {
  header: QuoteHeader
  lines: QuoteLine[]
}

// ---------------------------------------------------------------------------
// Invoices + pay by card
// ---------------------------------------------------------------------------

export type PaymentGroup = 'open' | 'paid' | 'overdue'
export type InvoiceListFilter = 'open' | 'paid' | 'all'

export interface InvoiceHeader {
  id: string
  docNum: string | null
  docDate: string | null
  dueDate: string | null
  docTotal: number | null
  paidToDate: number | null
  balanceDue: number | null
  paymentStatus: string | null
  paymentGroup: PaymentGroup
  paymentStatusLabel: string
  currency: string
  stripeStatus: string | null
  stripeHostedUrl: string | null
}

/** GET /v1/client/invoices?status=open|paid|all — 200. */
export interface InvoicesListResponse {
  invoices: InvoiceHeader[]
  openBalance: number
}

export interface InvoiceLine {
  id: string
  itemCode: string | null
  description: string | null
  quantity: number | null
  unitPrice: number | null
  lineTotal: number | null
  uom: string | null
}

export interface InvoicePayment {
  id: string
  date: string | null
  amount: number | null
  method: string | null
}

/** GET /v1/client/invoices/:id — 200 (header nested, unlike orders). */
export interface InvoiceDetailResponse {
  header: InvoiceHeader
  lines: InvoiceLine[]
  payments: InvoicePayment[]
  relatedOrderIds: string[]
}

/** POST /v1/client/invoices/:id/pay-link — 200 (409 nothing_due). */
export interface PayLinkResponse {
  url: string
  stripeInvoiceId: string
  status: string | null
  feeAmount: number
  totalWithFee: number
  alreadyExisted: boolean
}

// ---------------------------------------------------------------------------
// Product catalog (purchase-request wizard) — pack-option price breaks
// ---------------------------------------------------------------------------

/**
 * One pack option for a product — each active Price_Break__c row IS a pack:
 * packSizeKg is the kg content of one pack (5 = "5kg Pail", 180 = "180kg
 * Drum"), uomName is its label, and pricePerKg is the per-kg price when
 * buying that pack (bigger packs are cheaper per kg). Rows arrive packSizeKg
 * ASC. There are no volume-tier semantics.
 */
export interface PriceBreakTier {
  id: string
  packSizeKg: number
  pricePerKg: number
  uomName: string | null
}

export type StockBadge = 'in_stock' | 'made_to_order'

export interface ProductHit {
  id: string
  name: string
  itemCode: string | null
  family: string | null
  leadTimeWeeks: number | null
  stockBadge: StockBadge
  tiers: PriceBreakTier[]
}

/** GET /v1/client/products/search?q= — 200 (409 no_pricebook). */
export interface ProductSearchResponse {
  products: ProductHit[]
}

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------

export interface AccountAddress {
  id: string
  kind: 'billing' | 'shipping'
  /** SAP AddressName — the code the order release sends as ShipToCode/PayToCode. */
  code: string
  street: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  country: string | null
  isDefaultShipTo: boolean
}

export interface AccountColleague {
  id: string
  name: string
  email: string
  isYou: boolean
}

/** GET /v1/client/account — 200. */
export interface AccountResponse {
  name: string
  cardCode: string | null
  paymentTerms: string | null
  creditLimit: number | null
  minimumShelfLifeDays: number | null
  addresses: AccountAddress[]
  colleagues: AccountColleague[]
}

// ---------------------------------------------------------------------------
// Credit application + trade references
// ---------------------------------------------------------------------------

/**
 * PDF §9 entity checkboxes, verbatim — these literal strings are also the
 * Salesforce picklist values (mirrors the API's exported const).
 */
export const ENTITY_TYPES = [
  'Corporation',
  'General Partnership',
  'Limited Partnership',
  'LLC',
  'Sole Proprietorship',
  'Other',
] as const
export type EntityType = (typeof ENTITY_TYPES)[number]

export interface CreditApplicationCompany {
  companyName: string
  companyAddress: string
  dba?: string
  phone: string
  /** Federal Tax ID or SSN — submit-only, never echoed back by any GET. */
  federalTaxId: string
  duns?: string
  typeOfBusiness?: string
  employeeCount?: number
  /** YYYY-MM-DD */
  dateEstablished?: string
  productsToPurchase?: string
  entityType: EntityType
  entityTypeOther?: string
  stateOrganized?: string
  authorizedPersons?: string
}

export interface CreditApplicationApContact {
  name: string
  phone?: string
  email: string
  invoiceEmail?: string
}

export interface CreditApplicationBank {
  bankName: string
  /** Submit-only, never echoed back. */
  accountNumber: string
  contactName?: string
  address?: string
  phone?: string
}

export interface CreditApplicationDisclosures {
  bankruptcy: boolean
  bankruptcyDetails?: string
  litigation: boolean
  litigationDetails?: string
}

export interface CreditApplicationAttestation {
  signedName: string
  signedTitle: string
  /** YYYY-MM-DD */
  signedDate: string
  termsAccepted: true
}

export interface CreditReferenceInput {
  name: string
  address?: string
  phone?: string
  fax?: string
  email: string
}

/** POST /v1/client/credit-application — request body (2–4 references). */
export interface SubmitCreditApplicationPayload {
  company: CreditApplicationCompany
  apContact: CreditApplicationApContact
  bank: CreditApplicationBank
  disclosures: CreditApplicationDisclosures
  attestation: CreditApplicationAttestation
  references: CreditReferenceInput[]
}

/** POST /v1/client/credit-application — 201 (per-reference send outcome). */
export interface SubmitCreditApplicationResponse {
  id: string
  name: string
  references: { id: string; sent: boolean }[]
}

export interface CreditApplicationReference {
  id: string
  referenceName: string
  email: string
  /** Raw Request_Status__c — Pending / Sent / Completed / Cancelled. */
  status: string
  sentAt: string | null
  completedAt: string | null
}

export interface CreditApplicationSummary {
  id: string
  name: string
  /** Raw Status__c — Draft → Submitted → References Requested → Under Review → Approved | Declined. */
  status: string
  submittedAt: string | null
  references: CreditApplicationReference[]
}

/** GET /v1/client/credit-application — 200 (null when none exists yet). */
export interface CreditApplicationResponse {
  application: CreditApplicationSummary | null
}

/** POST /v1/client/credit-application/references/:refId/resend — 200 (409 already_completed). */
export interface ResendReferenceResponse {
  sent: true
}

// Q4–Q6 answer sets, verbatim from the trade-reference PDF — these literal
// strings are also the Salesforce picklist values (mirrors the API's exported
// consts). Do not "fix" the spacing.
export const PAYMENT_PROMPTNESS = [
  'Always on time (100%)',
  'Nearly always on time (90%+)',
  'Often on time (50% - 90%)',
  'Not often on time (less than 50%)',
] as const
export type PaymentPromptness = (typeof PAYMENT_PROMPTNESS)[number]

export const LATE_PAYMENT_SEVERITY = [
  'Never pays late',
  'Less than 30 days',
  '30 to 60 days',
  'More than 60 days',
] as const
export type LatePaymentSeverity = (typeof LATE_PAYMENT_SEVERITY)[number]

export const CUSTOMER_RATING = ['Excellent', 'Above Average', 'Average', 'Poor'] as const
export type CustomerRating = (typeof CUSTOMER_RATING)[number]

export interface TradeRefereePrefill {
  name?: string
  company?: string
  phone?: string
  email?: string
}

/** GET /v1/public/trade-reference/:token — 200 (404 not_found, 410 link_expired). */
export type PublicTradeReferenceResponse =
  | { status: 'completed' }
  | {
      status: 'open'
      customerName: string
      referenceName: string
      refereePrefill: TradeRefereePrefill
    }

export interface TradeReferee {
  name: string
  title: string
  company: string
  phone?: string
  address?: string
  email: string
}

/** POST /v1/public/trade-reference/:token — request body. */
export interface SubmitTradeReferencePayload {
  referee: TradeReferee
  /** Q1 — free text, e.g. "4 years". */
  accountLength: string
  /** Q2 — currency amount; the paper form tolerates a blank, so does the API. */
  averageMonthlyTurnover?: number
  /** Q3 — free text, e.g. "Net 30". */
  creditTerms: string
  paymentPromptness: PaymentPromptness
  latePaymentSeverity: LatePaymentSeverity
  customerRating: CustomerRating
  /** Q7 */
  comments?: string
}

/** POST /v1/public/trade-reference/:token — 200 (409 already_completed). */
export interface SubmitTradeReferenceResponse {
  ok: true
}

// ---------------------------------------------------------------------------
// Customer intake (public tokenized form — the welcome-packet link)
// ---------------------------------------------------------------------------

// Estimated_Annual_Volume__c picklist, ASCII BY DESIGN: the printed intake
// PDF uses en-dashes ("1–5 MT"), but the Salesforce picklist values (and the
// API's zod enum) use plain hyphens — special characters break picklist
// metadata deploys. Do not "fix" these back to match the paper form (mirrors
// the API's exported const in routes/public/intake.ts).
export const ANNUAL_VOLUMES = [
  'Less than 1 MT',
  '1-5 MT',
  '5-10 MT',
  '10 MT or more',
] as const
export type AnnualVolume = (typeof ANNUAL_VOLUMES)[number]

/** Regions__c multiselect values; 'Other' pairs with regionsOther free text. */
export const REGIONS = ['North America', 'EU', 'Asia', 'Other'] as const
export type Region = (typeof REGIONS)[number]

/**
 * The four fixed contact rows of the intake form — the submission must carry
 * exactly one contact per role (mirrors the API's exported const).
 */
export const CONTACT_ROLES = ['Ordering', 'Technical', 'Accounts Payable', 'Samples'] as const
export type ContactRole = (typeof CONTACT_ROLES)[number]

/** Company identity Green Line already knows — seeds the open form. */
export interface IntakePrefill {
  legalCompanyName?: string
  website?: string
}

/** GET /v1/public/intake/:token — 200 (404 not_found, 410 link_expired). */
export type PublicIntakeResponse =
  | { status: 'completed' }
  | {
      status: 'open'
      companyName: string
      contactName: string
      prefill: IntakePrefill
    }

export interface IntakeCompany {
  legalName: string
  dba?: string
  /** Shown on invoices, shipping documents, and order confirmations. */
  preferredDocumentName?: string
  billingAddress: string
  website?: string
}

export interface IntakeRoleContact {
  role: ContactRole
  name: string
  email: string
  phone?: string
}

export interface IntakeAdditionalContact {
  name: string
  title?: string
  email?: string
  phone?: string
}

export interface IntakeShipping {
  primaryShipTo: string
  /** Up to 4. */
  additionalShipTos: string[]
  sampleShipTo?: string
  specialDeliveryRequirements?: string
}

export interface IntakeOverview {
  primaryProductApplications: string
  estimatedAnnualVolume: AnnualVolume
  /** At least one. */
  regions: Region[]
  regionsOther?: string
}

/** POST /v1/public/intake/:token — request body (roleContacts: each role exactly once). */
export interface SubmitIntakePayload {
  company: IntakeCompany
  roleContacts: IntakeRoleContact[]
  /** Up to 3. */
  additionalContacts: IntakeAdditionalContact[]
  shipping: IntakeShipping
  overview: IntakeOverview
}

/** POST /v1/public/intake/:token — 200 (409 already_completed). */
export interface SubmitIntakeResponse {
  ok: true
}

// ---------------------------------------------------------------------------
// Requests (Case-backed) — customer service cases submitted + tracked in the
// Hub, managed end-to-end in Salesforce. Status labels/groups/chips live in
// src/lib/status.ts (CASE_STATUS_LABELS et al.).
// ---------------------------------------------------------------------------

/** The portal form types (Request_Type__c is broader — staff-only values exist). */
export type CaseRequestType =
  | 'document_request'
  | 'questionnaire'
  | 'quality_issue'
  | 'technical'
  | 'regulatory'
  | 'other'

/**
 * Document kinds a document request can ask for. These compose into the
 * case Description server-side — there is no dedicated Salesforce field.
 */
export const DOC_TYPES = ['COA', 'TDS', 'SDS', 'Spec', 'Statement', 'Other'] as const
export type DocType = (typeof DOC_TYPES)[number]

export type CaseStatusGroup = 'open' | 'closed'

/** ?scope= on the list — 'mine' = cases you submitted OR participate on. */
export type CaseListScope = 'mine' | 'all'
/** ?status= on the list. */
export type CaseListFilter = 'open' | 'closed' | 'all'

export interface CaseSummary {
  id: string
  /** Server-minted org case number (zero-padded, e.g. "00003712"). */
  caseNumber: string
  /** Server-composed per form type on create (user-authored only for question types). */
  subject: string
  /**
   * Request_Type__c slug. Portal-creatable values are CaseRequestType, but
   * the API also emits read-only slugs for staff-created cases ('pricing',
   * 'sample') and passes unknown picklist values through verbatim — always
   * render via caseTypeLabel() (src/lib/status.ts), never index a closed
   * record.
   */
  type: string
  /** Raw Case Status — labels/groups/chips come from src/lib/status.ts. */
  status: string
  /** Server-derived from status (mirror: caseStatusLabel). */
  statusLabel: string
  /** Server-derived from status (mirror: caseStatusGroup) — closed only when Closed. */
  statusGroup: CaseStatusGroup
  /** Server-derived: status is 'Waiting on Customer' — the list pins + ambers these. */
  waitingOnYou: boolean
  /** Server-derived: you submitted it OR you're a participant (the mine union). */
  mine: boolean
  submittedByName: string | null
  createdAt: string
  updatedAt: string
  /** YYYY-MM-DD — questionnaire requests only. */
  dueDate: string | null
  onBehalfOfBrand: string | null
  /** Server-derived from the Product__c lookup. */
  productName: string | null
}

/** GET /v1/client/cases?scope=mine|all&status=open|closed|all — 200. */
export interface CasesListResponse {
  /** Sorted by updatedAt descending; truncated at the server's 500-row limit. */
  cases: CaseSummary[]
  /** Computed within the requested scope, before the status filter. */
  counts: { open: number; closed: number; all: number }
  /** True when the scope holds more cases than the server list limit returned. */
  capped: boolean
}

export interface CaseParticipant {
  contactId: string
  name: string
  email: string
  isYou: boolean
  /** The case's submitting contact — always listed, never removable. */
  isSubmitter: boolean
}

export interface CaseFileEntry {
  /** ContentDocument id — the download route takes this. */
  docId: string
  name: string
  /** Lowercased, no dot (server-derived from the name). */
  extension: string
  sizeBytes: number
  uploadedAt: string
  /**
   * Uploader display name. Files uploaded via the Hub ride the org's Hub
   * integration user and always read "Green Line Hub" — uploadedByYou is
   * what marks yours; staff uploads carry the staff member's name.
   */
  uploadedByName: string
  /** Server-derived: uploaded through your Hub session. */
  uploadedByYou: boolean
}

export type CaseCommentSide = 'you' | 'greenline'
/** Email direction from Green Line's perspective: outbound = Green Line → you. */
export type CaseEmailDirection = 'inbound' | 'outbound'

export interface CaseCreatedEntry {
  kind: 'created'
  id: string
  at: string
}

export interface CaseCommentEntry {
  kind: 'comment'
  id: string
  at: string
  /** Server-derived from the author (HUB_SF_USER_ID = you; staff = greenline). */
  side: CaseCommentSide
  /**
   * Attribution display string, server-composed. Hub-side comments read
   * "{Name} ({Account})" — e.g. "Maya Chen (Lumen Botanica)" — or
   * "{Actor} (Green Line) for {Name}" on impersonated sessions. Green Line
   * staff comments carry the author's bare name (side already says so).
   */
  authorName: string
  body: string
}

export interface CaseStatusEntry {
  kind: 'status'
  id: string
  at: string
  /** Raw Case Status the case moved to. */
  toStatus: string
  toLabel: string
  waitingOnYou: boolean
}

export interface CaseEmailEntry {
  kind: 'email'
  id: string
  at: string
  direction: CaseEmailDirection
  subject: string
  fromName: string
  /** Server-sanitized — the SPA still sanitizes again before rendering. */
  bodyHtml: string
}

export interface CaseFileTimelineEntry {
  kind: 'file'
  id: string
  at: string
  docId: string
  name: string
  uploadedByName: string
}

/**
 * One timeline event — the server assembles these from CaseFeed, CaseHistory,
 * and EmailMessage rows (collapsing status changes whose label didn't change)
 * and returns them newest-first.
 */
export type CaseTimelineEntry =
  | CaseCreatedEntry
  | CaseCommentEntry
  | CaseStatusEntry
  | CaseEmailEntry
  | CaseFileTimelineEntry

/** GET /v1/client/cases/:id — 200 (404 collapses unknown AND foreign ids). */
export interface CaseDetail extends CaseSummary {
  /** Server-composed on create (structured per form type). */
  description: string | null
  /** Raw Resolution__c — closed cases only. */
  resolution: string | null
  externalReference: string | null
  lotNumber: string | null
  /** Submitter first, then added participants. */
  participants: CaseParticipant[]
  /** Sorted by uploadedAt descending. */
  files: CaseFileEntry[]
  timeline: CaseTimelineEntry[]
}

interface CreateCaseCommonFields {
  /** The end brand when ordering on behalf of one (distributor-mediated). */
  onBehalfOfBrand?: string
  /** Your own reference number for this request (max 100 chars). */
  externalReference?: string
  /** Contact ids of account colleagues to copy in — validated server-side. */
  participants?: string[]
}

export interface CreateDocumentRequestPayload extends CreateCaseCommonFields {
  type: 'document_request'
  productId: string
  lotNumber?: string
  /** At least one — composed into the Description server-side. */
  docTypes: DocType[]
  details?: string
}

export interface CreateQuestionnairePayload extends CreateCaseCommonFields {
  type: 'questionnaire'
  description: string
  /** YYYY-MM-DD — the one form type with a due date. */
  dueDate?: string
}

export interface CreateQualityIssuePayload extends CreateCaseCommonFields {
  type: 'quality_issue'
  productId: string
  lotNumber: string
  description: string
}

export interface CreateQuestionPayload extends CreateCaseCommonFields {
  type: 'technical' | 'regulatory' | 'other'
  subject: string
  description: string
  productId?: string
}

/**
 * The `payload` JSON field of the multipart POST /v1/client/cases (file parts
 * ride alongside under `files`). The server derives everything else: case
 * number, status (New), owner queue, subject for the structured types, and
 * the composed Description.
 */
export type CreateCasePayload =
  | CreateDocumentRequestPayload
  | CreateQuestionnairePayload
  | CreateQualityIssuePayload
  | CreateQuestionPayload

/** Per-file outcome — a failed file never fails the whole request. */
export interface CaseFileResult {
  name: string
  ok: boolean
  /** file_type_not_allowed | file_too_large | upload_failed. */
  error?: string
}

/** POST /v1/client/cases (multipart: payload + up to 5 files) — 201. */
export interface CreateCaseResponse {
  id: string
  caseNumber: string
  files: CaseFileResult[]
}

/** POST /v1/client/cases/:id/participants and its DELETE — 200 (idempotent). */
export interface CaseParticipantsResponse {
  participants: CaseParticipant[]
}

/** POST /v1/client/cases/:id/files (multipart, up to 5 files) — 200. */
export interface CaseFileUploadResponse {
  files: CaseFileResult[]
}

// Server-enforced caps on the case endpoints — the forms mirror them
// client-side (src/lib/files.ts) and the MSW handlers enforce them so tests
// catch drift. MAX_CASE_FILES is per submission (create or upload batch),
// not a lifetime per-case cap.
export const MAX_CASE_FILES = 5
export const MAX_CASE_FILE_BYTES = 10 * 1024 * 1024 // 10 MB
export const MAX_COMMENT_LENGTH = 4000
/** Case.Lot_Number__c is Text(80) — the API 400s anything longer. */
export const MAX_LOT_NUMBER_LENGTH = 80
/** Document-request details land in a LongTextArea(2000) — ditto. */
export const MAX_CASE_DETAILS_LENGTH = 2000

/** Generic authenticated error bodies. */
export interface ApiErrorBody {
  error: string
  message?: string
  /** `invalid_pack` only: 0-based index into the submitted lines array. */
  lineIndex?: number
}
